import { memo, useEffect, useRef, useState } from 'react';

import { type ProcessingStatus } from '@/components/chat';
import { ProcessingProgressBar } from '@/components/ui/processing-progress-bar';
import { useChatAnalytics } from '@/hooks/chat/useChatAnalytics';
import { trackMessageProcessingSurfaced } from '@/util/analyticsEvents';

interface MessageProcessingProgressProps {
  /** Twilio SID of the message being processed. Used as the analytics
   * dedupe key so the impression event fires at most once per message
   * mount even as the status advances through its intermediate values. */
  messageSid: string;
  /** Conversation id for analytics joins. */
  conversationId: string;
  /** Latest backend-emitted processing status. `undefined` means the
   * backend hasn't written a status yet — we're in the just-sent grace
   * window. */
  status: Exclude<ProcessingStatus, 'complete'> | undefined;
}

type NonTerminalStatus = Exclude<ProcessingStatus, 'complete'>;

/** Internal stage key. `pending` covers the grace window between Send
 * and the first backend `pipelineStatus`. */
type StageKey = 'pending' | NonTerminalStatus;

/** Pool of copy options for each non-terminal stage. One line per
 * stage is picked independently at random on mount, so a single run's
 * sequence is assembled from across the voices rather than locked to
 * one tonal set. Tone target: deferential, attentive, lightly witty —
 * addressed to the expert who just submitted. */
const STAGE_LABEL_POOLS: Record<StageKey, readonly string[]> = {
  pending: [
    'Got it — settling in to read',
    'Received with thanks — one moment',
    'Thank you — one moment with this',
    'Received — sharpening our pencil',
    'Got it — settling in',
    'Thanks — sitting with this for a beat',
    "Thanks — we'll get right on it",
  ],
  reviewing: [
    'Hanging on your every word',
    'Taking in your expertise',
    'Reading slowly, the way it deserves',
    'Reading every line you wrote',
    'Listening closely',
    'Reading your answer with care',
    'Reading thoughtfully',
  ],
  analyzing: [
    'Soaking in the clinical detail',
    'Sitting with your clinical reasoning',
    'Taking in the clinical detail',
    'Bookmarking the clinical takeaways',
    'Catching the clinical points',
    'Following each point with care',
    'Pulling out the clinical pearls',
  ],
  linking: [
    'Threading it back to the submitter',
    'Pairing this with what was asked',
    'Bringing it home to the question',
    'Bringing it back to the original ask',
    'Connecting it to the question asked',
    'Setting it next to the question',
    'Tying it to what was asked',
  ],
};

/** Progress (0–1) per stage. Values are perceived rather than measured —
 * spaced so each stage feels like meaningful forward motion. */
const STATUS_TO_PROGRESS: Record<NonTerminalStatus, number> = {
  reviewing: 0.33,
  analyzing: 0.66,
  linking: 0.9,
};

const PENDING_PROGRESS = 0.1;

/** Minimum time a stage label stays visible before the bar is allowed
 * to advance to the next backend status. Keeps the copy readable when
 * the backend rips through stages faster than a human can take them in. */
const MIN_STAGE_DISPLAY_MS = 1500;

function pickRandomLabel(pool: readonly string[]): string {
  return pool[Math.floor(Math.random() * pool.length)];
}

function pickStageLabels(): Record<StageKey, string> {
  return {
    pending: pickRandomLabel(STAGE_LABEL_POOLS.pending),
    reviewing: pickRandomLabel(STAGE_LABEL_POOLS.reviewing),
    analyzing: pickRandomLabel(STAGE_LABEL_POOLS.analyzing),
    linking: pickRandomLabel(STAGE_LABEL_POOLS.linking),
  };
}

function stageKeyFromStatus(status: NonTerminalStatus | undefined): StageKey {
  return status ?? 'pending';
}

function progressForStage(stage: StageKey): number {
  return stage === 'pending' ? PENDING_PROGRESS : STATUS_TO_PROGRESS[stage];
}

/**
 * Author-side progress bar rendered below the outgoing message bubble
 * while the backend processes the expert's answer. Wraps the generic
 * `ProcessingProgressBar` with chat-specific status→progress mapping
 * and the single-impression analytics dedupe.
 *
 * Two pieces of per-mount state shape what the expert sees:
 *
 * 1. **Per-stage random labels.** One line is picked independently
 *    from each stage's pool in `STAGE_LABEL_POOLS` on mount and held
 *    for the lifetime of the bar. Re-renders don't re-roll; a fresh
 *    message gets a fresh roll for every stage.
 * 2. **Min-display latch.** The displayed stage trails the incoming
 *    `status` prop so each label is shown for at least
 *    `MIN_STAGE_DISPLAY_MS`. If the backend skips ahead during the
 *    latch window, we jump to the newest known status when the timer
 *    fires — we don't replay skipped intermediate stages.
 *
 * Mount/unmount is owned by the caller (`renderBubble`), which gates
 * on the feature flag + same `isPending` predicate as before.
 */
export const MessageProcessingProgress = memo(function MessageProcessingProgress({
  messageSid,
  conversationId,
  status,
}: MessageProcessingProgressProps) {
  const { track } = useChatAnalytics();

  const hasFiredImpressionRef = useRef(false);

  const [labels] = useState(pickStageLabels);

  const [displayedStage, setDisplayedStage] = useState<StageKey>(() => stageKeyFromStatus(status));

  const stageShownAtRef = useRef<number>(Date.now());

  const pendingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (hasFiredImpressionRef.current || status === undefined) {
      return;
    }

    hasFiredImpressionRef.current = true;

    trackMessageProcessingSurfaced(track, {
      messageSid,
      conversationId,
      status,
    });
  }, [track, messageSid, conversationId, status]);

  useEffect(() => {
    const targetStage = stageKeyFromStatus(status);

    if (targetStage === displayedStage) {
      return;
    }

    const elapsedSinceShown = Date.now() - stageShownAtRef.current;

    const remaining = MIN_STAGE_DISPLAY_MS - elapsedSinceShown;

    if (pendingTimerRef.current) {
      clearTimeout(pendingTimerRef.current);

      pendingTimerRef.current = null;
    }

    if (remaining <= 0) {
      stageShownAtRef.current = Date.now();

      setDisplayedStage(targetStage);

      return;
    }

    pendingTimerRef.current = setTimeout(() => {
      pendingTimerRef.current = null;

      stageShownAtRef.current = Date.now();

      setDisplayedStage(targetStage);
    }, remaining);

    return () => {
      if (pendingTimerRef.current) {
        clearTimeout(pendingTimerRef.current);

        pendingTimerRef.current = null;
      }
    };
  }, [status, displayedStage]);

  const progress = progressForStage(displayedStage);

  const message = labels[displayedStage];

  return (
    <ProcessingProgressBar className="mt-2 w-full px-1" progress={progress} message={message} />
  );
});
